import React, { useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styled from 'styled-components';
import {
  Upload,
  X,
  Image as ImageIcon,
  Video,
  File,
  Plus,
  Check,
  AlertCircle,
  Clock,
  Tag
} from 'lucide-react';
import { MediaUploadRequest } from '../../types/api';
import { useMidias, useFileValidation, useKeywordSuggestions } from '../../hooks/useMidias';

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${({ theme }) => theme.colors.overlay};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: ${({ theme }) => theme.spacing[6]};
`;

const ModalContent = styled(motion.div)`
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.lg};
  box-shadow: ${({ theme }) => theme.shadows.xl};
  max-width: 650px;
  width: 100%;
  max-height: 70vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  margin: auto;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    max-height: 95vh;
    max-width: 95vw;
  }
`;

const ModalHeader = styled.div`
  background: ${({ theme }) => theme.colors.backgroundTertiary};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  padding: ${({ theme }) => theme.spacing[4]};
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: ${({ theme }) => theme.spacing[3]};
  }
`;

const ModalTitle = styled.h2`
  font-family: ${({ theme }) => theme.typography.fontFamily.heading};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  transition: all ${({ theme }) => theme.animations.duration.fast} ${({ theme }) => theme.animations.easing.easeInOut};

  &:hover {
    background: ${({ theme }) => theme.colors.backgroundTertiary};
    color: ${({ theme }) => theme.colors.textPrimary};
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const ModalBody = styled.div`
  padding: ${({ theme }) => theme.spacing[5]};
  overflow-y: auto;
  flex: 1;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: ${({ theme }) => theme.spacing[3]};
  }
`;

const ModalFooter = styled.div`
  background: ${({ theme }) => theme.colors.backgroundTertiary};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  padding: ${({ theme }) => theme.spacing[4]};
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing[3]};
  flex-shrink: 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: ${({ theme }) => theme.spacing[3]};
  }
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[5]};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${({ theme }) => theme.animations.duration.fast} ${({ theme }) => theme.animations.easing.easeInOut};
  border: 1px solid transparent;

  ${({ $variant, theme }) => {
    if ($variant === 'primary') {
      return `
        background: ${theme.colors.primary};
        color: ${theme.colors.textPrimary};
        &:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: ${theme.shadows.neon};
        }
      `;
    }
    return `
      background: ${theme.colors.backgroundSecondary};
      color: ${theme.colors.textSecondary};
      border-color: ${theme.colors.border};
      &:hover {
        background: ${theme.colors.backgroundTertiary};
        color: ${theme.colors.textPrimary};
        border-color: ${theme.colors.primary};
      }
    `;
  }}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none !important;
  }
`;

const Label = styled.label<{ $required?: boolean }>`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.textSecondary};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  margin-bottom: ${({ theme }) => theme.spacing[2]};
  ${({ $required, theme }) => $required ? `&::after { content: ' *'; color: ${theme.colors.error}; }` : ''}
`;

const Input = styled.input`
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[4]};
  background: ${({ theme }) => theme.colors.backgroundTertiary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  transition: all ${({ theme }) => theme.animations.duration.fast} ${({ theme }) => theme.animations.easing.easeInOut};
  width: 100%;

  &:focus {
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(255, 43, 43, 0.1);
    outline: none;
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.textTertiary};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Textarea = styled.textarea`
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[4]};
  background: ${({ theme }) => theme.colors.backgroundTertiary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-family: inherit;
  transition: all ${({ theme }) => theme.animations.duration.fast} ${({ theme }) => theme.animations.easing.easeInOut};
  width: 100%;
  resize: vertical;

  &:focus {
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(255, 43, 43, 0.1);
    outline: none;
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.textTertiary};
  }
`;

const DropZone = styled.div<{ $isDragOver: boolean; $hasFile: boolean }>`
  border: 2px dashed ${({ $isDragOver, $hasFile, theme }) =>
    $hasFile ? theme.colors.success :
    $isDragOver ? theme.colors.primary : theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.lg};
  padding: ${({ theme }) => theme.spacing[5]};
  text-align: center;
  transition: all ${({ theme }) => theme.animations.duration.fast} ${({ theme }) => theme.animations.easing.easeInOut};
  cursor: pointer;
  background: ${({ $isDragOver, theme }) =>
    $isDragOver ? `${theme.colors.primary}10` : 'transparent'};
  margin-bottom: ${({ theme }) => theme.spacing[4]};

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => `${theme.colors.primary}05`};
  }
`;

const DropZoneContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const DropZoneIcon = styled.div<{ $hasFile: boolean }>`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: ${({ $hasFile, theme }) =>
    $hasFile ? theme.colors.success : theme.colors.backgroundTertiary};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ $hasFile, theme }) =>
    $hasFile ? theme.colors.textPrimary : theme.colors.textSecondary};
  transition: all ${({ theme }) => theme.animations.duration.fast} ${({ theme }) => theme.animations.easing.easeInOut};
`;

const DropZoneText = styled.div`
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const DropZoneTitle = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  margin: 0;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const DropZoneSubtitle = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  margin: 0;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const HiddenInput = styled.input`
  display: none;
`;

const FilePreview = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => theme.spacing[4]};
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const FileIcon = styled.div<{ $type: 'video' | 'image' }>`
  width: 40px;
  height: 40px;
  border-radius: ${({ theme }) => theme.borders.radius.md};
  background: ${({ $type, theme }) =>
    $type === 'video' ? theme.colors.info : theme.colors.success};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const FileInfo = styled.div`
  flex: 1;
`;

const FileName = styled.p`
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0;
`;

const FileSize = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0;
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  padding: ${({ theme }) => theme.spacing[1]};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  transition: all ${({ theme }) => theme.animations.duration.fast} ${({ theme }) => theme.animations.easing.easeInOut};

  &:hover {
    background: ${({ theme }) => theme.colors.backgroundTertiary};
    color: ${({ theme }) => theme.colors.error};
  }
`;

const FormSection = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing[4]};

  &:last-child {
    margin-bottom: 0;
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing[4]};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const DelayInputContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const DelayInput = styled.input`
  width: 80px;
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
  background: ${({ theme }) => theme.colors.backgroundTertiary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  transition: all ${({ theme }) => theme.animations.duration.fast} ${({ theme }) => theme.animations.easing.easeInOut};
  /* Remove as setas do input number */
  &[type='number']::-webkit-inner-spin-button,
  &[type='number']::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  &[type='number'] {
    -moz-appearance: textfield;
    appearance: textfield;
  }
  &:focus {
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(255, 43, 43, 0.1);
    outline: none;
  }
`;

const DelayUnit = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const KeywordInputContainer = styled.div`
  position: relative;
`;

const SuggestionsContainer = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
  box-shadow: ${({ theme }) => theme.shadows.lg};
`;

const SuggestionItem = styled.button`
  width: 100%;
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
  text-align: left;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.textPrimary};
  cursor: pointer;
  transition: background ${({ theme }) => theme.animations.duration.fast} ${({ theme }) => theme.animations.easing.easeInOut};

  &:hover {
    background: ${({ theme }) => theme.colors.backgroundTertiary};
  }

  &:not(:last-child) {
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  }
`;

const IntentionsSection = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const IntentionsTitle = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0 0 ${({ theme }) => theme.spacing[2]} 0;
`;

const IntentionGroup = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing[2]};
`;

const IntentionLabel = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.textTertiary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: ${({ theme }) => theme.spacing[1]};
  display: block;
`;

const IntentionKeywords = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const ClickableKeyword = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: ${({ theme }) => `${theme.colors.primary}15`};
  color: ${({ theme }) => theme.colors.primary};
  border: 1px solid ${({ theme }) => `${theme.colors.primary}30`};
  border-radius: ${({ theme }) => theme.borders.radius.sm};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${({ theme }) => theme.animations.duration.fast} ${({ theme }) => theme.animations.easing.easeInOut};

  &:hover {
    transform: translateY(-1px);
    background: ${({ theme }) => `${theme.colors.primary}25`};
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const ErrorMessage = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  color: ${({ theme }) => theme.colors.error};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  padding: ${({ theme }) => theme.spacing[3]};
  background: ${({ theme }) => `${theme.colors.error}10`};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

interface MediaUploadProps {
  userId?: string;
  onClose?: () => void;
  onSuccess?: () => void;
}

const MediaUpload: React.FC<MediaUploadProps> = ({ userId, onClose, onSuccess }) => {
  const { uploadMedia, isUploading } = useMidias(userId);
  const { validateFile, getFileType, compressImage } = useFileValidation();
  const { getSuggestions, intentions } = useKeywordSuggestions(userId);

  const [file, setFile] = useState<File | null>(null);
  const [palavrasChave, setPalavrasChave] = useState('');
  const [descricao, setDescricao] = useState('');
  const [delaySegundos, setDelaySegundos] = useState(3);
  const [isDragOver, setIsDragOver] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isCompressing, setIsCompressing] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const suggestions = getSuggestions(palavrasChave);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length > 0) {
      handleFileSelect(droppedFiles[0]);
    }
  }, []);

  const handleFileSelect = async (selectedFile: File) => {
    const validation = validateFile(selectedFile);
    if (!validation.valid) {
      setError(validation.error!);
      return;
    }

    setError(null);

    // Comprimir imagem se for muito grande (>5MB)
    if (selectedFile.type.startsWith('image/') && selectedFile.size > 5 * 1024 * 1024) {
      setIsCompressing(true);
      try {
        const compressed = await compressImage(selectedFile);
        console.log(`[MediaUpload] Compressão: ${(selectedFile.size / 1024 / 1024).toFixed(2)}MB → ${(compressed.size / 1024 / 1024).toFixed(2)}MB`);
        setFile(compressed);
        setError(null);
      } catch (err) {
        console.error('[MediaUpload] Erro ao comprimir:', err);
        setError('Erro ao comprimir imagem. Tente um arquivo menor.');
      } finally {
        setIsCompressing(false);
      }
    } else {
      setFile(selectedFile);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      handleFileSelect(selectedFile);
    }
  };

  const removeFile = () => {
    setFile(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const addKeyword = (keyword: string) => {
    const currentKeywords = palavrasChave.split(',').map(k => k.trim()).filter(k => k);
    if (!currentKeywords.includes(keyword)) {
      const newKeywords = [...currentKeywords, keyword].join(', ');
      setPalavrasChave(newKeywords);
    }
    setShowSuggestions(false);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const canSubmit = Boolean(file && palavrasChave.trim() && !isUploading && !isCompressing && userId);

  const handleSubmit = async () => {
    if (!canSubmit) return;

    const request: MediaUploadRequest = {
      file: file!,
      palavras_chave: palavrasChave.trim(),
      descricao: descricao.trim() || undefined,
      delay_segundos: delaySegundos > 0 ? delaySegundos : undefined,
      userId,
    };

    uploadMedia(request, {
      onSuccess: () => {
        // Reset form
        setFile(null);
        setPalavrasChave('');
        setDescricao('');
        setDelaySegundos(3);
        setError(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        onSuccess?.();
      },
    });
  };

  return (
    <ModalOverlay
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      onClick={onClose}
    >
      <ModalContent
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ duration: 0.2 }}
        onClick={(e) => e.stopPropagation()}
      >
        <ModalHeader>
          <ModalTitle>
            <Upload size={18} />
            Adicionar Nova Mídia
          </ModalTitle>
          <CloseButton onClick={onClose}>
            <X size={16} />
          </CloseButton>
        </ModalHeader>

        <ModalBody>

      <DropZone
        $isDragOver={isDragOver}
        $hasFile={!!file}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <DropZoneContent>
          <DropZoneIcon $hasFile={!!file}>
            {file ? <Check size={24} /> : <Upload size={24} />}
          </DropZoneIcon>
          <DropZoneText>
            <DropZoneTitle>
              {isCompressing ? 'Comprimindo imagem...' : file ? 'Arquivo selecionado!' : 'Arraste um arquivo ou clique para selecionar'}
            </DropZoneTitle>
            <DropZoneSubtitle>
              Suporta: PNG, JPG, GIF, MP4, MOV, AVI (máx. 20MB)
            </DropZoneSubtitle>
          </DropZoneText>
        </DropZoneContent>
      </DropZone>

      <HiddenInput
        ref={fileInputRef}
        type="file"
        accept="image/*,video/*"
        onChange={handleFileInputChange}
      />

      <AnimatePresence>
        {file && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            <FilePreview>
              <FileIcon $type={getFileType(file)}>
                {getFileType(file) === 'video' ? <Video size={20} /> : <ImageIcon size={20} />}
              </FileIcon>
              <FileInfo>
                <FileName>{file.name}</FileName>
                <FileSize>{formatFileSize(file.size)}</FileSize>
              </FileInfo>
              <RemoveButton onClick={removeFile}>
                <X size={20} />
              </RemoveButton>
            </FilePreview>
          </motion.div>
        )}
      </AnimatePresence>

      {error && (
        <ErrorMessage>
          <AlertCircle size={16} />
          {error}
        </ErrorMessage>
      )}

      <FormSection>
        <Label $required>Palavras-chave</Label>
        <KeywordInputContainer>
          <Input
            placeholder="venda, comprar, promoção (separadas por vírgula)"
            value={palavrasChave}
            onChange={(e) => {
              setPalavrasChave(e.target.value);
              setShowSuggestions(e.target.value.length > 2);
            }}
            onFocus={() => setShowSuggestions(palavrasChave.length > 2)}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
          />
          <AnimatePresence>
            {showSuggestions && suggestions.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
              >
                <SuggestionsContainer>
                  {suggestions.map((suggestion, index) => (
                    <SuggestionItem
                      key={index}
                      onClick={() => addKeyword(suggestion)}
                    >
                      {suggestion}
                    </SuggestionItem>
                  ))}
                </SuggestionsContainer>
              </motion.div>
            )}
          </AnimatePresence>
        </KeywordInputContainer>
      </FormSection>

      <IntentionsSection>
        <IntentionsTitle>Sugestões por intenção (clique para adicionar):</IntentionsTitle>
        {Object.entries(intentions).map(([intention, keywords]) => (
          <IntentionGroup key={intention}>
            <IntentionLabel>{intention}</IntentionLabel>
            <IntentionKeywords>
              {keywords.slice(0, 6).map((keyword, index) => (
                <ClickableKeyword
                  key={index}
                  onClick={() => addKeyword(keyword)}
                >
                  <Plus size={10} />
                  {keyword}
                </ClickableKeyword>
              ))}
            </IntentionKeywords>
          </IntentionGroup>
        ))}
      </IntentionsSection>

      <FormSection>
        <Label>Descrição (opcional)</Label>
        <Textarea
          rows={3}
          placeholder="Descrição da mídia para facilitar identificação..."
          value={descricao}
          onChange={(e) => setDescricao(e.target.value)}
        />
      </FormSection>

      <FormSection>
        <Label>Delay de envio</Label>
        <DelayInputContainer>
          <Clock size={16} />
          <DelayInput
            type="number"
            min="1"
            max="60"
            value={delaySegundos}
            onChange={(e) => setDelaySegundos(Number(e.target.value))}
          />
          <DelayUnit>segundos</DelayUnit>
        </DelayInputContainer>
      </FormSection>
        </ModalBody>

        <ModalFooter>
          <Button $variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            $variant="primary"
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            {isCompressing ? 'Comprimindo...' : isUploading ? 'Enviando...' : 'Adicionar Mídia'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </ModalOverlay>
  );
};

export default MediaUpload;